import { PhoneNumberUtil } from 'google-libphonenumber';

import type { PhoneVerificationErrorReason } from './sign-up-phone-verification';

const phoneUtil = PhoneNumberUtil.getInstance();

export const VALID_CAPTCHA_STATUS = 'valid-captcha';

// Vague on purpose: gives an attacker nothing to tune, and a v3 score won't change on an immediate retry.
export const RECAPTCHA_FAILURE_MESSAGE = "We couldn't send your verification code. Try again in a few minutes, or contact support if it keeps happening.";

// Retry once; a failure is usually a transient token fetch, not a persistent condition.
export async function fetchV3TokenWithRetry(executeRecaptcha: (action: string) => Promise<string>, action: string): Promise<string> {
  try {
    return await executeRecaptcha(action);
  } catch {
    return executeRecaptcha(action);
  }
}

// Verify failures all return 401, differentiated only by the body message. Send-side 400/429 go to mapSendErrorReason.
export function mapVerifyErrorReason(error: unknown): PhoneVerificationErrorReason {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  const message = typeof data === 'string' ? data : ((data as { error?: string; message?: string })?.error ?? (data as { message?: string })?.message ?? '');

  if (message === 'Too many attempts') {
    return 'rate_limited';
  }
  if (message === 'Code expired' || message === 'Code already used' || message === 'Invalid or expired code') {
    return 'expired';
  }
  if (message === 'Invalid code') {
    return 'invalid_code';
  }
  return 'unknown';
}

export function mapSendErrorReason(error: unknown): { reason: PhoneVerificationErrorReason; cooldownSeconds: number } {
  const status = (error as { response?: { status?: number } })?.response?.status;
  // 400: number can't receive a code (landline, region disabled, invalid) — terminal.
  if (status === 400) {
    return { reason: 'send_unavailable', cooldownSeconds: 0 };
  }
  // 429: short resend cooldown, distinct from the verify attempt cap ('rate_limited').
  if (status === 429) {
    const retryAfter = (error as { response?: { data?: { retryAfterSeconds?: number } } })?.response?.data?.retryAfterSeconds;
    return { reason: 'send_cooldown', cooldownSeconds: typeof retryAfter === 'number' ? retryAfter : 0 };
  }
  return { reason: 'unknown', cooldownSeconds: 0 };
}

// Parse rather than regex so country codes of any length (+1, +61, +353) are
// extracted correctly; a fixed-width pattern exposes area-code digits on +1.
// Stored numbers are not reliably canonical — the +CC0 variant (a national trunk 0 left after the
// country code) is the same number to link-profiles but a different string. Compare on the parsed
// country code + national number so a formatting difference doesn't read as a changed number.
export function isSamePhoneNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return !a && !b;
  }
  if (a === b) {
    return true;
  }

  try {
    const parsedA = phoneUtil.parse(a);
    const parsedB = phoneUtil.parse(b);
    return parsedA.getCountryCode() === parsedB.getCountryCode() && parsedA.getNationalNumber() === parsedB.getNationalNumber();
  } catch {
    return false;
  }
}

export function maskPhone(phone: string): string {
  let parsed;
  try {
    parsed = phoneUtil.parse(phone);
  } catch {
    return phone;
  }

  const countryCode = `+${parsed.getCountryCode()}`;
  const national = String(parsed.getNationalNumber() ?? '');
  if (national.length < 4) {
    return `${countryCode} ${national}`;
  }

  const firstDigit = national.slice(0, 1);
  const lastThree = national.slice(-3);
  const hidden = '•'.repeat(national.length - 4);
  const grouped = `${firstDigit}${hidden}`.replace(/(.{3})/g, '$1 ').trim();

  return `${countryCode} ${grouped} ${lastThree}`;
}
